import React from 'react';
import {Tooltip} from '../Tooltip';

export type UserStatus = 'active' | 'disabled' | 'pending';
export type OrganisationStatus = 'active' | 'inactive';

interface StatusBadgeProps {
  type: 'user' | 'organisation';
  enabled?: boolean;
  acceptedTerms?: boolean;
  active?: boolean;
  showTooltip?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClasses = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2 py-1 text-xs',
  lg: 'px-3 py-1 text-sm',
};

export function StatusBadge({type, enabled, acceptedTerms, active, showTooltip = true, size = 'md'}: StatusBadgeProps) {
  if (type === 'user') {
    const tooltipContent = showTooltip ? (
      <div className="space-y-1.5 min-w-[160px]">
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-200">Enabled:</span>
          <span className={`font-semibold ${enabled ? 'text-green-400' : 'text-red-400'}`}>{enabled ? '✓ Yes' : '✗ No'}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-200">Accepted Terms:</span>
          <span className={`font-semibold ${acceptedTerms ? 'text-green-400' : 'text-red-400'}`}>{acceptedTerms ? '✓ Yes' : '✗ No'}</span>
        </div>
      </div>
    ) : null;

    let badge;
    if (!enabled) {
      badge = <span className={`${sizeClasses[size]} font-medium rounded-full bg-red-100 text-red-800 cursor-help`}>Disabled</span>;
    } else if (!acceptedTerms) {
      badge = <span className={`${sizeClasses[size]} font-medium rounded-full bg-yellow-100 text-yellow-800 cursor-help`}>Pending</span>;
    } else {
      badge = <span className={`${sizeClasses[size]} font-medium rounded-full bg-green-100 text-green-800 cursor-help`}>Active</span>;
    }

    if (showTooltip && tooltipContent) {
      return (
        <Tooltip content={tooltipContent} placement="left">
          {badge}
        </Tooltip>
      );
    }

    return badge;
  }

  // Organisation status
  if (!active) {
    return (
      <span className={`${sizeClasses[size]} font-semibold rounded-full bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400`}>Inactive</span>
    );
  }

  return (
    <span className={`${sizeClasses[size]} font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400`}>
      Active
    </span>
  );
}
